import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import ScrollToTop from './components/ScrollToTop';
import { useAuth } from './contexts/AuthContext';

// Import pages
import Dashboard from './pages/dashboard';
import Login from './pages/login';
import SignUp from './pages/user-registration/SignUp';

import DocumentBaseManagement from './pages/document-base-management';
import DocumentEditor from './pages/document-editor';
import UserManagement from './pages/user-management';
import SystemSettings from './pages/system-settings';
import ChangeManagement from './pages/change-management';
import SearchResults from './pages/search-results';
import AIAnalysisDashboard from './pages/ai-analysis-dashboard';
import AskZentri from './pages/ask-zentri';
import NotFound from './pages/NotFound';
import ResetPassword from 'pages/reset-password';

// Error fallback component
function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold text-destructive mb-4">Something went wrong</h2>
        <p className="text-muted-foreground mb-4">{error?.message}</p>
        <button
          onClick={resetErrorBoundary}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Public route wrapper (redirects authenticated users)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function Routes() {
  return (
    <BrowserRouter>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <ScrollToTop />
        <RouterRoutes>
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/dashboard" element={<Dashboard />} />
          
          {/* Protected routes */}
          <Route 
            path="/document-base-management" 
            element={
              <ProtectedRoute>
                <DocumentBaseManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/document-editor" 
            element={
              <ProtectedRoute>
                <DocumentEditor 
                  document={null}
                  onSave={() => {}}
                  saving={false}
                  documentType=""
                  content=""
                  onChange={() => {}}
                  highlightLocation={null}
                  onHighlightCleared={() => {}}
                />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/user-management" 
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/system-settings" 
            element={
              <ProtectedRoute>
                <SystemSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/change-management" 
            element={
              <ProtectedRoute>
                <ChangeManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/search-results" 
            element={
              <ProtectedRoute>
                <SearchResults />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ai-analysis-dashboard" 
            element={
              <ProtectedRoute>
                <AIAnalysisDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ask-zentri" 
            element={
              <ProtectedRoute>
                <AskZentri />
              </ProtectedRoute>
            } 
          />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default Routes;